import React, { useEffect } from "react";

const SEO = ({
  title,
  description,
  canonicalUrl, // preferred explicit canonical
  ogUrl, // optional alias for canonical
  ogImage,
  ogImageAlt,
  ogType = "website",
  articleAuthor,
  articlePublishedTime,
  articleModifiedTime,
  articleSection,
  noindex = false,
  schema,
  breadcrumbs, // array of { name, url } for BreadcrumbList
}) => {
  const siteTitle = "Recovery Compass NH";
  const defaultDescription =
    "Comprehensive recovery resources, treatment centers, and support services across New Hampshire. Find help for substance use disorders, peer support, and family resources.";
  const baseUrl = "https://www.recoverycompassnh.com";

  const fullTitle = title
    ? title.includes(siteTitle)
      ? title
      : `${title} | ${siteTitle}`
    : siteTitle;
  const metaDescription = description || defaultDescription;
  const imageAlt =
    ogImageAlt || title || "Recovery Compass NH - Recovery Resources";

  // Strip query + hash so OG/Canonical stay clean
  const cleanUrl = (raw) => {
    try {
      const u = new URL(raw);
      u.search = "";
      u.hash = "";
      return u.toString();
    } catch {
      return raw;
    }
  };

  const rawUrl =
    canonicalUrl ||
    ogUrl ||
    (typeof window !== "undefined" ? window.location.href : baseUrl);

  const url = cleanUrl(rawUrl);

  // Ensure absolute image URL (accepts absolute OR site-relative like "/articles.png")
  const image = (() => {
    if (!ogImage) return `${baseUrl}/og-default.png`;
    if (/^https?:\/\//i.test(ogImage)) return ogImage;
    const path = ogImage.startsWith("/") ? ogImage : `/${ogImage}`;
    return `${baseUrl}${path}`;
  })();

  // Build BreadcrumbList schema from breadcrumbs prop
  const breadcrumbSchema =
    breadcrumbs && breadcrumbs.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: breadcrumbs.map((crumb, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: crumb.name,
            item: crumb.url,
          })),
        }
      : null;

  // Combine all schemas into an array
  const allSchemas = (() => {
    const arr = [];
    if (schema) {
      if (Array.isArray(schema)) arr.push(...schema);
      else arr.push(schema);
    }
    if (breadcrumbSchema) arr.push(breadcrumbSchema);
    return arr;
  })();

  useEffect(() => {
    document.getElementById("directory-schema")?.remove();
    const upsert = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      }
      if (attrs.content) el.setAttribute("content", attrs.content);
    };

    // title & canonical
    document.title = fullTitle;
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", url);

    // basic + OG
    upsert('meta[name="description"]', {
      name: "description",
      content: metaDescription,
    });
    upsert('meta[property="og:title"]', {
      property: "og:title",
      content: fullTitle,
    });
    upsert('meta[property="og:description"]', {
      property: "og:description",
      content: metaDescription,
    });
    upsert('meta[property="og:type"]', {
      property: "og:type",
      content: ogType,
    });
    upsert('meta[property="og:url"]', { property: "og:url", content: url });
    upsert('meta[property="og:image"]', {
      property: "og:image",
      content: image,
    });
    upsert('meta[property="og:image:width"]', {
      property: "og:image:width",
      content: "1200",
    });
    upsert('meta[property="og:image:height"]', {
      property: "og:image:height",
      content: "630",
    });
    upsert('meta[property="og:image:alt"]', {
      property: "og:image:alt",
      content: imageAlt,
    });

    // twitter
    upsert('meta[name="twitter:card"]', {
      name: "twitter:card",
      content: "summary_large_image",
    });
    upsert('meta[name="twitter:title"]', {
      name: "twitter:title",
      content: fullTitle,
    });
    upsert('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: metaDescription,
    });
    upsert('meta[name="twitter:image"]', {
      name: "twitter:image",
      content: image,
    });
    upsert('meta[name="twitter:image:alt"]', {
      name: "twitter:image:alt",
      content: imageAlt,
    });
  }, [fullTitle, metaDescription, ogType, url, image, imageAlt]);

  return (
    <>
      {/* Title */}
      <title>{fullTitle}</title>

      {/* Basic Meta */}
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={url} />

      {/* Robots - advanced directives for rich snippets */}
      {noindex ? (
        <meta name="robots" content="noindex,nofollow" />
      ) : (
        <meta
          name="robots"
          content="index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1"
        />
      )}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={imageAlt} />
      <meta property="og:site_name" content={siteTitle} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={imageAlt} />

      {/* Article-specific tags */}
      {articleAuthor && (
        <meta property="article:author" content={articleAuthor} />
      )}
      {articlePublishedTime && (
        <meta
          property="article:published_time"
          content={articlePublishedTime}
        />
      )}
      {articleModifiedTime && (
        <meta property="article:modified_time" content={articleModifiedTime} />
      )}
      {articleSection && (
        <meta property="article:section" content={articleSection} />
      )}

      {/* Misc */}
      <meta name="theme-color" content="#06b6d4" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta
        name="apple-mobile-web-app-status-bar-style"
        content="black-translucent"
      />
      <meta name="geo.region" content="US-NH" />
      <meta name="geo.placename" content="New Hampshire" />
      <meta name="ICBM" content="43.1939,-71.5724" />

      {/* JSON-LD - all schemas */}
      {allSchemas.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              allSchemas.length === 1 ? allSchemas[0] : allSchemas
            ),
          }}
        />
      )}
    </>
  );
};

// ── Schema helpers ──────────────────────────────────────────────

export const generateOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Recovery Compass NH",
  alternateName: "RCNH",
  description:
    "Comprehensive recovery resources and support services across New Hampshire",
  url: "https://www.recoverycompassnh.com",
  logo: {
    "@type": "ImageObject",
    url: "https://www.recoverycompassnh.com/RecoveryCompassLogo.png",
    width: 512,
    height: 512,
  },
  image: "https://www.recoverycompassnh.com/og-default.png",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Laconia",
    addressRegion: "NH",
    postalCode: "03246",
    addressCountry: "US",
  },
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "Customer Service",
    email: "recoverycompassnh@gmail.com",
    availableLanguage: "English",
    areaServed: {
      "@type": "State",
      name: "New Hampshire",
      sameAs: "https://en.wikipedia.org/wiki/New_Hampshire",
    },
  },
  sameAs: ["https://www.facebook.com/profile.php?id=61559004021648"],
  areaServed: {
    "@type": "State",
    name: "New Hampshire",
    sameAs: "https://en.wikipedia.org/wiki/New_Hampshire",
  },
  knowsAbout: [
    "Addiction Recovery",
    "Substance Use Disorder Treatment",
    "Mental Health Resources",
    "Veteran Services",
    "Crisis Support",
    "Peer Support",
    "Recovery Housing",
  ],
});

export const generateWebSiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Recovery Compass NH",
  alternateName: "RCNH",
  url: "https://www.recoverycompassnh.com",
  description:
    "New Hampshire's comprehensive recovery resource hub for substance use disorders, mental health, and veteran services",
  publisher: {
    "@type": "Organization",
    name: "Recovery Compass NH",
    logo: {
      "@type": "ImageObject",
      url: "https://www.recoverycompassnh.com/RecoveryCompassLogo.png",
    },
  },
  inLanguage: "en-US",
});

export const generateArticleSchema = (article) => {
  const baseUrl = "https://www.recoverycompassnh.com";
  const categoryLabels = {
    "unsung-heroes": "Unsung Heroes",
    "stories-of-recovery": "Stories of Recovery",
    "blog-posts": "Blog Posts",
  };

  return {
    "@context": "https://schema.org",
    "@type": article.category === "blog-posts" ? "BlogPosting" : "Article",
    headline: article.title,
    description: article.excerpt,
    image:
      article.socialImageUrl ||
      article.imageUrl ||
      `${baseUrl}/og-default.png`,
    author: article.author
      ? { "@type": "Person", name: article.author }
      : { "@type": "Organization", name: "Recovery Compass NH", url: baseUrl },
    publisher: {
      "@type": "Organization",
      name: "Recovery Compass NH",
      url: baseUrl,
      logo: {
        "@type": "ImageObject",
        url: `${baseUrl}/RecoveryCompassLogo.png`,
        width: 512,
        height: 512,
      },
    },
    datePublished: article.date,
    dateModified: article.dateModified || article.date,
    articleSection: categoryLabels[article.category] || "Articles",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${baseUrl}/articles/${article.slug || article.id}`,
    },
    isAccessibleForFree: true,
    inLanguage: "en-US",
  };
};

export const generateWebPageSchema = (pageData) => ({
  "@context": "https://schema.org",
  "@type": pageData.type || "WebPage",
  name: pageData.title,
  description: pageData.description,
  url: pageData.url,
  isPartOf: {
    "@type": "WebSite",
    name: "Recovery Compass NH",
    url: "https://www.recoverycompassnh.com",
  },
  about: {
    "@type": "Thing",
    name: pageData.aboutName || "Addiction Recovery Resources",
    description:
      pageData.aboutDescription ||
      "Information and resources for substance abuse recovery",
  },
  audience: {
    "@type": "Audience",
    audienceType:
      pageData.audienceType ||
      "People seeking addiction recovery resources in New Hampshire",
  },
  isAccessibleForFree: true,
  inLanguage: "en-US",
  ...(pageData.lastReviewed && { lastReviewed: pageData.lastReviewed }),
  ...(pageData.speciality && {
    specialty: { "@type": "Specialty", name: pageData.speciality },
  }),
});

export const generateFAQSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  })),
});

export const generateItemListSchema = (items, listName) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: listName,
  numberOfItems: items.length,
  itemListElement: items.map((item, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: item.title,
    url: `https://www.recoverycompassnh.com/articles/${
      item.slug || item.id
    }`,
  })),
});

export const generateSpeakableSchema = (url, cssSelectors) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  speakable: {
    "@type": "SpeakableSpecification",
    cssSelector: cssSelectors || ["h1", "[data-speakable]"],
  },
  url: url,
});

export default SEO;
